
import { useState } from "react";
import { useAuth } from "@/contexts/auth";
import { UserRole } from "@/types";
import { UserRole as UserRoleEnum } from "@/types/enums";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Building, Users, ListChecks, ShieldCheck, Crown } from "lucide-react";

export function RoleSwitch() {
  const { user, switchRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  
  if (!user) return null;
  
  // Only show the role switch if user is app_owner
  const canSwitchRoles = user.role === UserRoleEnum.APP_OWNER;
  
  if (!canSwitchRoles) return null;
  
  const handleRoleChange = (role: UserRole) => {
    if (role !== user.role) {
      switchRole(role as UserRole);
    }
    setIsOpen(false);
  };

  return (
    <div className="flex items-center">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            <RoleIcon role={user.role as UserRole} className="mr-2 h-4 w-4" />
            <span className="hidden md:inline">Switch Role</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleRoleChange(UserRoleEnum.DEPARTMENT_HEAD as UserRole)}>
            <Building className="mr-2 h-4 w-4" />
            <span>Department Head</span>
            {user.role === UserRoleEnum.DEPARTMENT_HEAD && <span className="ml-2 text-xs text-muted-foreground">(current)</span>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleRoleChange(UserRoleEnum.PLANT_MANAGER as UserRole)}>
            <Users className="mr-2 h-4 w-4" />
            <span>Plant Manager</span>
            {user.role === UserRoleEnum.PLANT_MANAGER && <span className="ml-2 text-xs text-muted-foreground">(current)</span>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleRoleChange(UserRoleEnum.HR as UserRole)}>
            <ListChecks className="mr-2 h-4 w-4" />
            <span>HR</span>
            {user.role === UserRoleEnum.HR && <span className="ml-2 text-xs text-muted-foreground">(current)</span>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleRoleChange(UserRoleEnum.SUPER_ADMIN as UserRole)}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            <span>Super Admin</span>
            {user.role === UserRoleEnum.SUPER_ADMIN && <span className="ml-2 text-xs text-muted-foreground">(current)</span>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleRoleChange(UserRoleEnum.APP_OWNER as UserRole)}>
            <Crown className="mr-2 h-4 w-4" />
            <span>App Owner</span>
            {user.role === UserRoleEnum.APP_OWNER && <span className="ml-2 text-xs text-muted-foreground">(current)</span>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function RoleIcon({ role, className }: { role: UserRole, className?: string }) {
  switch(role) {
    case UserRoleEnum.DEPARTMENT_HEAD:
      return <Building className={className} />;
    case UserRoleEnum.PLANT_MANAGER:
      return <Users className={className} />;
    case UserRoleEnum.HR:
      return <ListChecks className={className} />;
    case UserRoleEnum.SUPER_ADMIN:
      return <ShieldCheck className={className} />;
    case UserRoleEnum.APP_OWNER:
      return <Crown className={className} />;
    default:
      return null;
  }
}

export default RoleSwitch;
